import { supabase } from "@/shared/lib/supabase"
import type { Database } from "@/shared/types/database.types"
import {
  FAKE_AVERAGE_METRICS,
  FAKE_AHT_DISTRIBUTION,
  getFakeMonthlyVolumes,
  mergeMonthlyVolumes,
  mergeAverageMetrics,
  mergeAHTDistribution,
} from "@/shared/lib/fake-stats"

// Type aliases for convenience
type CalculationStatistics = Database["public"]["Views"]["calculation_statistics"]["Row"]

/**
 * Fetches calculation statistics for charts
 */
export async function getCalculationStatistics(): Promise<CalculationStatistics | null> {
  try {
    const { data, error } = await supabase
      .from("calculation_statistics")
      .select("*")
      .single<CalculationStatistics>()

    if (error) {
      console.error("Error fetching statistics:", error)
      return null
    }

    return data as CalculationStatistics
  } catch (error) {
    console.error("Error fetching statistics:", error)
    return null
  }
}

/**
 * Get monthly calculation volumes for the chart
 * Merges fake baseline data with real database values
 */
export async function getMonthlyVolumes(): Promise<Array<{
  month: string
  monthKey: string
  runs: number
}> | null> {
  try {
    const stats = await getCalculationStatistics()
    const realVolumes = stats?.monthly_volumes as Array<{
      month: string
      calculation_count: number
    }> | null

    // Get fake baseline data
    const fakeVolumes = getFakeMonthlyVolumes()

    // Merge fake data with real data
    const mergedVolumes = mergeMonthlyVolumes(fakeVolumes, realVolumes)

    const monthKeyMap: Record<string, string> = {
      "01": "jan",
      "02": "feb",
      "03": "mar",
      "04": "apr",
      "05": "may",
      "06": "jun",
      "07": "jul",
      "08": "aug",
      "09": "sep",
      "10": "oct",
      "11": "nov",
      "12": "dec",
    }

    // Helper to safely extract month key from YYYY-MM format
    const getMonthKey = (monthStr: string): string => {
      // Validate format: YYYY-MM
      const monthRegex = /^\d{4}-(\d{2})$/
      const match = monthStr.match(monthRegex)

      if (!match || !match[1]) {
        console.warn(`Invalid month format: ${monthStr}, defaulting to 'jan'`)
        return "jan"
      }

      return monthKeyMap[match[1]] || "jan"
    }

    const mapped = mergedVolumes.map((item) => ({
      month: item.month,
      monthKey: getMonthKey(item.month),
      runs: item.calculation_count,
    }))

    return mapped
  } catch (error) {
    console.error("Error fetching monthly volumes:", error)
    // Return fake data on error
    const fakeVolumes = getFakeMonthlyVolumes()
    const monthKeyMap: Record<string, string> = {
      "01": "jan",
      "02": "feb",
      "03": "mar",
      "04": "apr",
      "05": "may",
      "06": "jun",
      "07": "jul",
      "08": "aug",
      "09": "sep",
      "10": "oct",
      "11": "nov",
      "12": "dec",
    }
    const getMonthKey = (monthStr: string): string => {
      const monthRegex = /^\d{4}-(\d{2})$/
      const match = monthStr.match(monthRegex)
      if (!match || !match[1]) return "jan"
      return monthKeyMap[match[1]] || "jan"
    }
    return fakeVolumes.map((item) => ({
      month: item.month,
      monthKey: getMonthKey(item.month),
      runs: item.calculation_count,
    }))
  }
}

/**
 * Get average metrics for the statistics panel
 * Merges fake baseline data with real database values
 */
export async function getAverageMetrics(): Promise<{
  handleTime: number
  serviceLevel: number
  answerTime: number
  shrinkage: number
  occupancy: number
} | null> {
  try {
    const stats = await getCalculationStatistics()
    const realMetrics = stats?.average_metrics as {
      avg_handle_time: number | null
      avg_service_level: number | null
      avg_answer_time: number | null
      avg_shrinkage: number | null
      avg_occupancy: number | null
    } | null

    // Merge fake data with real data
    const mergedMetrics = mergeAverageMetrics(FAKE_AVERAGE_METRICS, realMetrics)

    const normalized = {
      handleTime: mergedMetrics.avg_handle_time,
      serviceLevel: mergedMetrics.avg_service_level,
      answerTime: mergedMetrics.avg_answer_time,
      shrinkage: mergedMetrics.avg_shrinkage,
      occupancy: mergedMetrics.avg_occupancy,
    }

    return normalized
  } catch (error) {
    console.error("Error fetching average metrics:", error)
    // Return fake data on error
    return {
      handleTime: FAKE_AVERAGE_METRICS.avg_handle_time,
      serviceLevel: FAKE_AVERAGE_METRICS.avg_service_level,
      answerTime: FAKE_AVERAGE_METRICS.avg_answer_time,
      shrinkage: FAKE_AVERAGE_METRICS.avg_shrinkage,
      occupancy: FAKE_AVERAGE_METRICS.avg_occupancy,
    }
  }
}

/**
 * Get AHT distribution for the chart
 * Merges fake baseline data with real database values
 */
export async function getAHTDistribution(): Promise<Array<{
  rangeKey: string
  percentage: number
}> | null> {
  try {
    const stats = await getCalculationStatistics()
    const realDistribution = stats?.aht_distribution as Array<{
      range_key: string
      percentage: number
      count: number
    }> | null

    // Merge fake data with real data
    const mergedDistribution = mergeAHTDistribution(FAKE_AHT_DISTRIBUTION, realDistribution)

    const mapped = mergedDistribution.map((item) => ({
      rangeKey: item.range_key,
      percentage: item.percentage,
    }))

    return mapped
  } catch (error) {
    console.error("Error fetching AHT distribution:", error)
    // Return fake data on error
    return FAKE_AHT_DISTRIBUTION.map((item) => ({
      rangeKey: item.range_key,
      percentage: item.percentage,
    }))
  }
}
